import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import './ResourceGanttChart.css';
import OperationModal from './OperationModal';
import ResourceModal from './ResourceModal';

const ResourceGanttChart = ({
                                resources = [],
                                operations = [],
                                onResourceClick,
                                onOperationClick
                            }) => {
    // Размеры и пагинация
    const [viewportWidth, setViewportWidth] = useState(1200);
    const [viewportHeight, setViewportHeight] = useState(600);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');

    // Новые состояния для расширенных фильтров
    const [activeFilters, setActiveFilters] = useState([]);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });

    // Масштаб и режим отображения - только три значения
    const [zoomLevel, setZoomLevel] = useState(1); // 1 = 100%
    const [displayMode, setDisplayMode] = useState('days');

    // Прокрутка
    const [scrollPosition, setScrollPosition] = useState({ left: 0, top: 0 });
    const [initialScrollDone, setInitialScrollDone] = useState(false);

    // Модальные окна
    const [selectedOperation, setSelectedOperation] = useState(null);
    const [selectedResource, setSelectedResource] = useState(null);

    // Рефы
    const leftPanelRef = useRef(null);
    const diagramContainerRef = useRef(null);
    const timeScaleContainerRef = useRef(null);
    const chartContainerRef = useRef(null);
    const containerRef = useRef(null);
    const rightPanelRef = useRef(null);
    const timeScaleInnerRef = useRef(null);
    const mainAreaRef = useRef(null);
    const dropdownRef = useRef(null);
    const filterButtonRefs = useRef({});

    // Флаги для блокировки рекурсивной синхронизации
    const isSyncingScroll = useRef(false);
    const lastScrollTime = useRef(0);
    const scrollTimeoutRef = useRef(null);

    // Константы
    const ROW_HEIGHT = 60;
    const TIME_HEADER_HEIGHT = 90;
    const INFINITE_SCROLL_MULTIPLIER = 5; // Увеличиваем в 5 раз для бесконечной прокрутки

    // Фиксированные значения масштаба
    const ZOOM_LEVELS = {
        '75%': 0.75,
        '100%': 1,
        '175%': 1.75
    };

    // Типы фильтров
    const FILTER_TYPES = {
        TYPE: 'type',
        STATUS: 'status',
        DATE_RANGE: 'date_range',
        SEARCH: 'search',
        OPERATION_COUNT: 'operation_count'
    };

    // Опции для фильтров
    const filterOptions = useMemo(() => ({
        [FILTER_TYPES.TYPE]: [
            { id: 'personnel', label: 'Персонал', icon: '👤', color: '#4facfe' },
            { id: 'equipment', label: 'Оборудование', icon: '🔧', color: '#667eea' },
            { id: 'tool', label: 'Инструменты', icon: '📐', color: '#f093fb' },
            { id: 'other', label: 'Другие', icon: '📦', color: '#6b7280' }
        ],
        [FILTER_TYPES.STATUS]: [
            { id: '1', label: 'Выполнено', icon: '✅', color: '#10b981' },
            { id: '2', label: 'В ожидании', icon: '⏳', color: '#6b7280' },
            { id: '3', label: 'В процессе', icon: '🔄', color: '#f59e0b' },
            { id: '4', label: 'Ошибка', icon: '❌', color: '#ef4444' },
            { id: '5', label: 'На паузе', icon: '⏸️', color: '#eab308' },
            { id: '6', label: 'Планируется', icon: '📅', color: '#3b82f6' },
            { id: '7', label: 'Отменено', icon: '🚫', color: '#8b5cf6' }
        ],
        [FILTER_TYPES.OPERATION_COUNT]: [
            { id: 'none', label: 'Без операций', icon: '0️⃣', range: [0, 0] },
            { id: 'few', label: '1-3 операции', icon: '🔢', range: [1, 3] },
            { id: 'some', label: '4-10 операций', icon: '📊', range: [4, 10] },
            { id: 'many', label: 'Более 10', icon: '📈', range: [11, Infinity] }
        ]
    }), []);

    // Текущий уровень масштаба в процентах (для отображения)
    const currentZoomPercent = useMemo(() => {
        return Math.round(zoomLevel * 100) + '%';
    }, [zoomLevel]);

    // Ширина единицы времени с учетом фиксированных масштабов
    const TIME_UNIT_WIDTH = useMemo(() => {
        const baseWidth = 40; // Базовая ширина при 100%

        switch(displayMode) {
            case 'years':
                return baseWidth * zoomLevel;
            case 'days':
                return baseWidth * zoomLevel;
            case 'hours':
                return baseWidth * zoomLevel * 0.5; // Часы в 2 раза уже
            default:
                return baseWidth * zoomLevel;
        }
    }, [displayMode, zoomLevel]);

    // Обновление режима при изменении масштаба
    useEffect(() => {
        if (Math.abs(zoomLevel - ZOOM_LEVELS['75%']) < 0.01) {
            setDisplayMode('years');
        } else if (Math.abs(zoomLevel - ZOOM_LEVELS['100%']) < 0.01) {
            setDisplayMode('days');
        } else if (Math.abs(zoomLevel - ZOOM_LEVELS['175%']) < 0.01) {
            setDisplayMode('hours');
        }
    }, [zoomLevel]);

    // Закрытие выпадающего меню при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                // Проверяем, не кликнули ли на кнопку открытия фильтра
                const isFilterButton = Object.values(filterButtonRefs.current).some(
                    ref => ref && ref.contains(event.target)
                );

                if (!isFilterButton) {
                    setOpenDropdown(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Добавление/удаление фильтров
    const addFilter = useCallback((filterType, value, label, icon) => {
        setActiveFilters(prev => {
            // Проверяем, не добавлен ли уже такой фильтр
            const exists = prev.some(filter =>
                filter.type === filterType && filter.value === value
            );

            if (exists) return prev;

            // Если это фильтр по типу, удаляем старый фильтр типа
            if (filterType === FILTER_TYPES.TYPE) {
                const filtered = prev.filter(filter => filter.type !== FILTER_TYPES.TYPE);
                return [...filtered, { type: filterType, value, label, icon, id: `${filterType}_${value}` }];
            }

            // Если это поиск, удаляем старый поиск
            if (filterType === FILTER_TYPES.SEARCH) {
                const filtered = prev.filter(filter => filter.type !== FILTER_TYPES.SEARCH);
                return [...filtered, { type: filterType, value, label, icon, id: `${filterType}_${Date.now()}` }];
            }

            return [...prev, { type: filterType, value, label, icon, id: `${filterType}_${value}` }];
        });

        setOpenDropdown(null);
    }, [FILTER_TYPES.TYPE, FILTER_TYPES.SEARCH]);

    const removeFilter = useCallback((filterId) => {
        setActiveFilters(prev => prev.filter(filter => filter.id !== filterId));
    }, []);

    const clearAllFilters = useCallback(() => {
        setActiveFilters([]);
        setSearchQuery('');
        setTypeFilter('all');
    }, []);

    // Обработчик поиска
    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
        if (query.trim()) {
            addFilter(
                FILTER_TYPES.SEARCH,
                query,
                `Поиск: "${query}"`,
                '🔍'
            );
        } else {
            // Удаляем фильтр поиска
            setActiveFilters(prev => prev.filter(filter => filter.type !== FILTER_TYPES.SEARCH));
        }
    }, [addFilter, FILTER_TYPES.SEARCH]);

    // Обработчик фильтра по типу
    const handleTypeFilter = useCallback((type, label, icon) => {
        setTypeFilter(type);
        if (type !== 'all') {
            addFilter(FILTER_TYPES.TYPE, type, label, icon);
        } else {
            // Удаляем фильтр по типу
            setActiveFilters(prev => prev.filter(filter => filter.type !== FILTER_TYPES.TYPE));
        }
    }, [addFilter, FILTER_TYPES.TYPE]);

    // Обработчик открытия выпадающего меню
    const handleFilterButtonClick = useCallback((filterType, event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({
            x: rect.left,
            y: rect.bottom + window.scrollY
        });

        setOpenDropdown(openDropdown === filterType ? null : filterType);
    }, [openDropdown]);

    // Проверяем, активен ли фильтр
    const isFilterActive = useCallback((filterType, value) => {
        return activeFilters.some(filter => filter.type === filterType && filter.value === value);
    }, [activeFilters]);

    // Фильтрация ресурсов с учетом всех активных фильтров
    const filteredResources = useMemo(() => {
        let filtered = [...resources];

        // Применяем все активные фильтры
        activeFilters.forEach(filter => {
            switch(filter.type) {
                case FILTER_TYPES.TYPE:
                    filtered = filtered.filter(resource => resource.type === filter.value);
                    break;

                case FILTER_TYPES.SEARCH:
                    const query = filter.value.toLowerCase();
                    filtered = filtered.filter(resource =>
                        resource.title.toLowerCase().includes(query) ||
                        resource.id.toLowerCase().includes(query)
                    );
                    break;

                case FILTER_TYPES.STATUS:
                    // Фильтруем по статусу операций ресурса
                    filtered = filtered.filter(resource => {
                        const resourceOperations = operations.filter(op => op.resourceId === resource.id);
                        return resourceOperations.some(op => String(op.status) === filter.value);
                    });
                    break;

                case FILTER_TYPES.OPERATION_COUNT:
                    const option = filterOptions[FILTER_TYPES.OPERATION_COUNT].find(opt => opt.id === filter.value);
                    if (option) {
                        const [min, max] = option.range;
                        filtered = filtered.filter(resource => {
                            const operationCount = operations.filter(op => op.resourceId === resource.id).length;
                            if (max === Infinity) {
                                return operationCount >= min;
                            }
                            return operationCount >= min && operationCount <= max;
                        });
                    }
                    break;

                case FILTER_TYPES.DATE_RANGE:
                    // Фильтрация по диапазону дат операций
                    // (пока не реализовано полностью)
                    break;
            }
        });

        // Для обратной совместимости с старыми фильтрами
        if (typeFilter !== 'all') {
            filtered = filtered.filter(resource => resource.type === typeFilter);
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(resource =>
                resource.title.toLowerCase().includes(query) ||
                resource.id.toLowerCase().includes(query)
            );
        }

        return filtered;
    }, [resources, activeFilters, operations, typeFilter, searchQuery, filterOptions, FILTER_TYPES]);

    // Сортировка ресурсов
    const sortedResources = useMemo(() => {
        const typeOrder = { personnel: 1, equipment: 2, tool: 3, unknown: 4 };

        return [...filteredResources].sort((a, b) => {
            const typeA = typeOrder[a.type] || 999;
            const typeB = typeOrder[b.type] || 999;

            if (typeA !== typeB) {
                return typeA - typeB;
            }

            return a.title.localeCompare(b.title);
        });
    }, [filteredResources]);

    // Рассчитываем общее количество страниц
    const totalPages = Math.max(1, Math.ceil(sortedResources.length / pageSize));

    // Получаем ресурсы для текущей страницы
    const pagedResources = useMemo(() => {
        const startIndex = currentPage * pageSize;
        return sortedResources.slice(startIndex, startIndex + pageSize);
    }, [sortedResources, currentPage, pageSize]);

    // Получаем операции только для ресурсов на текущей странице
    const pagedOperations = useMemo(() => {
        const resourceIds = pagedResources.map(r => r.id);
        return operations.filter(op => resourceIds.includes(op.resourceId));
    }, [operations, pagedResources]);

    // Находим дату для начальной прокрутки - теперь для всех режимов
    const initialScrollDate = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (operations.length === 0) {
            return today;
        }

        // Собираем все даты операций
        const allDates = [];
        operations.forEach(op => {
            if (op.startDate) {
                const date = new Date(op.startDate);
                date.setHours(0, 0, 0, 0);
                allDates.push(date.getTime());
            }
            if (op.endDate) {
                const date = new Date(op.endDate);
                date.setHours(0, 0, 0, 0);
                allDates.push(date.getTime());
            }
        });

        if (allDates.length === 0) {
            return today;
        }

        // Сортируем даты
        allDates.sort((a, b) => a - b);

        // Находим ближайшую дату к сегодняшней (включая прошедшие)
        const todayTime = today.getTime();
        let closestDate = allDates[0];
        let minDiff = Math.abs(todayTime - closestDate);

        for (let i = 1; i < allDates.length; i++) {
            const diff = Math.abs(todayTime - allDates[i]);
            if (diff < minDiff) {
                minDiff = diff;
                closestDate = allDates[i];
            }
        }

        // Если есть операции сегодня или в будущем - показываем сегодня
        // Если все операции в прошлом - показываем ближайшую в прошлом
        const hasFutureOrToday = allDates.some(date => date >= todayTime);
        return hasFutureOrToday ? today : new Date(closestDate);
    }, [operations]);

    // Рассчитываем базовый временной диапазон операций с учетом начальной даты
    const baseTimeRange = useMemo(() => {
        const initialDate = initialScrollDate;
        let start = new Date(initialDate);
        let end = new Date(initialDate);

        if (operations.length === 0) {
            // Для всех режимов показываем одинаковый диапазон относительно начальной даты
            if (displayMode === 'years') {
                start.setFullYear(start.getFullYear() - 1);
                end.setFullYear(end.getFullYear() + 1);
            } else if (displayMode === 'hours') {
                start.setDate(start.getDate() - 2);
                end.setDate(end.getDate() + 2);
            } else {
                // Для режима дней показываем больше в будущее
                start.setDate(start.getDate() - 15);
                end.setDate(end.getDate() + 45);
            }

            return { start, end };
        }

        // Находим min и max даты из операций
        let minDate = new Date(operations[0].startDate);
        let maxDate = new Date(operations[0].endDate);

        operations.forEach(op => {
            if (op.startDate) {
                const startOp = new Date(op.startDate);
                if (startOp < minDate) minDate = startOp;
            }
            if (op.endDate) {
                const endOp = new Date(op.endDate);
                if (endOp > maxDate) maxDate = endOp;
            }
        });

        // Устанавливаем начальную дату как центр диапазона
        if (initialDate < minDate) {
            minDate = new Date(initialDate);
        }
        if (initialDate > maxDate) {
            maxDate = new Date(initialDate);
        }

        // Добавляем буфер
        if (displayMode === 'years') {
            minDate.setMonth(0, 1);
            minDate.setFullYear(minDate.getFullYear() - 1);
            maxDate.setMonth(11, 31);
            maxDate.setFullYear(maxDate.getFullYear() + 1);
        } else if (displayMode === 'days') {
            // Делаем больший буфер в прошлое, чтобы было что листать назад
            minDate.setDate(minDate.getDate() - 30);
            maxDate.setDate(maxDate.getDate() + 60);
        } else if (displayMode === 'hours') {
            minDate.setHours(0, 0, 0, 0);
            minDate.setDate(minDate.getDate() - 2);
            maxDate.setHours(23, 59, 59, 999);
            maxDate.setDate(maxDate.getDate() + 2);
        }

        return { start: minDate, end: maxDate };
    }, [operations, displayMode, initialScrollDate]);

    // Расширяем временной диапазон для бесконечной прокрутки
    const timeRange = useMemo(() => {
        if (!baseTimeRange.start || !baseTimeRange.end) return baseTimeRange;

        const start = new Date(baseTimeRange.start);
        const end = new Date(baseTimeRange.end);

        // Расширяем диапазон в INFINITE_SCROLL_MULTIPLIER раз
        const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        const extendedDays = totalDays * INFINITE_SCROLL_MULTIPLIER;
        const halfExtension = (extendedDays - totalDays) / 2;

        start.setDate(start.getDate() - halfExtension);
        end.setDate(end.getDate() + halfExtension);

        return { start, end };
    }, [baseTimeRange]);

    // Генерируем временные метки для всего расширенного диапазона
    const timeLabels = useMemo(() => {
        if (!timeRange.start || !timeRange.end) return [];

        const labels = [];
        const current = new Date(timeRange.start);
        const end = new Date(timeRange.end);

        if (displayMode === 'years') {
            current.setDate(1);
            current.setMonth(0);
            end.setDate(1);
            end.setMonth(0);
            end.setFullYear(end.getFullYear() + 1);

            while (current < end) {
                labels.push({
                    type: 'month',
                    date: new Date(current),
                    year: current.getFullYear(),
                    month: current.getMonth(),
                    timestamp: current.getTime()
                });
                current.setMonth(current.getMonth() + 1);
            }
        } else if (displayMode === 'days') {
            current.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);

            while (current <= end) {
                labels.push({
                    type: 'day',
                    date: new Date(current),
                    year: current.getFullYear(),
                    month: current.getMonth(),
                    day: current.getDate(),
                    dayOfWeek: current.getDay(),
                    timestamp: current.getTime()
                });
                current.setDate(current.getDate() + 1);
            }
        } else if (displayMode === 'hours') {
            current.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);

            const hours = [];
            let hourCount = 0;
            const maxHours = 24 * 365 * 2; // Ограничим максимум 2 года по часам

            while (current <= end && hourCount < maxHours) {
                for (let hour = 0; hour < 24; hour++) {
                    const hourDate = new Date(current);
                    hourDate.setHours(hour);
                    hours.push({
                        type: 'hour',
                        date: hourDate,
                        year: current.getFullYear(),
                        month: current.getMonth(),
                        day: current.getDate(),
                        hour: hour,
                        timestamp: hourDate.getTime()
                    });
                    hourCount++;
                }
                current.setDate(current.getDate() + 1);
                current.setHours(0, 0, 0, 0);
            }
            return hours;
        }

        return labels;
    }, [timeRange, displayMode]);

    // Находим индекс начальной даты для прокрутки - теперь для всех режимов
    const initialDateIndex = useMemo(() => {
        if (!timeRange.start || !timeLabels.length) return 0;

        const initialDate = initialScrollDate;
        const initialTime = initialDate.getTime();

        // Для режима лет/месяцев ищем ближайший месяц
        if (displayMode === 'years') {
            const initialYear = initialDate.getFullYear();
            const initialMonth = initialDate.getMonth();

            for (let i = 0; i < timeLabels.length; i++) {
                const label = timeLabels[i];
                if (label.type === 'month' &&
                    label.year === initialYear &&
                    label.month === initialMonth) {
                    return i;
                }
            }

            // Если не нашли точный месяц, ищем ближайший
            for (let i = 0; i < timeLabels.length; i++) {
                const label = timeLabels[i];
                if (label.type === 'month' && label.timestamp >= initialTime) {
                    return i;
                }
            }

            return 0;
        }

        // Для режима дней ищем ближайший день
        if (displayMode === 'days') {
            for (let i = 0; i < timeLabels.length; i++) {
                const label = timeLabels[i];
                if (label.type === 'day' && label.timestamp >= initialTime) {
                    return i;
                }
            }

            return 0;
        }

        // Для режима часов ищем ближайший час
        if (displayMode === 'hours') {
            for (let i = 0; i < timeLabels.length; i++) {
                const label = timeLabels[i];
                if (label.type === 'hour' && label.timestamp >= initialTime) {
                    return i;
                }
            }

            return 0;
        }

        return 0;
    }, [timeLabels, displayMode, initialScrollDate, timeRange.start]);

    // Группируем дни по месяцам для заголовков (только для режима дней)
    const monthHeaders = useMemo(() => {
        if (displayMode !== 'days' || timeLabels.length === 0) return [];

        const headers = [];
        let currentMonth = null;
        let startIndex = 0;
        let daysInMonth = 0;

        timeLabels.forEach((item, index) => {
            if (item.type !== 'day') return;

            const monthKey = item.year + '-' + item.month;

            if (monthKey !== currentMonth) {
                if (currentMonth !== null) {
                    headers.push({
                        month: new Date(timeLabels[startIndex].date),
                        startIndex,
                        endIndex: index - 1,
                        daysCount: daysInMonth
                    });
                }
                currentMonth = monthKey;
                startIndex = index;
                daysInMonth = 1;
            } else {
                daysInMonth++;
            }
        });

        // Добавляем последний месяц
        if (currentMonth !== null) {
            headers.push({
                month: new Date(timeLabels[startIndex].date),
                startIndex,
                endIndex: timeLabels.length - 1,
                daysCount: daysInMonth
            });
        }

        return headers;
    }, [timeLabels, displayMode]);

    // Группируем месяцы по годам для заголовков (только для режима лет)
    const yearHeaders = useMemo(() => {
        if (displayMode !== 'years' || timeLabels.length === 0) return [];

        const headers = [];
        let currentYear = null;
        let startIndex = 0;
        let monthsInYear = 0;

        timeLabels.forEach((item, index) => {
            if (item.type !== 'month') return;

            const yearKey = item.year.toString();

            if (yearKey !== currentYear) {
                if (currentYear !== null) {
                    headers.push({
                        year: new Date(timeLabels[startIndex].date),
                        startIndex,
                        endIndex: index - 1,
                        monthsCount: monthsInYear
                    });
                }
                currentYear = yearKey;
                startIndex = index;
                monthsInYear = 1;
            } else {
                monthsInYear++;
            }
        });

        // Добавляем последний год
        if (currentYear !== null) {
            headers.push({
                year: new Date(timeLabels[startIndex].date),
                startIndex,
                endIndex: timeLabels.length - 1,
                monthsCount: monthsInYear
            });
        }

        return headers;
    }, [timeLabels, displayMode]);

    const formatMonthHeader = (date) => {
        return date.toLocaleDateString('ru-RU', {
            month: 'long',
            year: 'numeric'
        });
    };

    const formatYearHeader = (date) => {
        return date.toLocaleDateString('ru-RU', {
            year: 'numeric'
        });
    };

    // Рассчитываем ширину контейнера
    const containerWidth = useMemo(() => {
        return timeLabels.length * TIME_UNIT_WIDTH;
    }, [timeLabels, TIME_UNIT_WIDTH]);

    // Рассчитываем высоту контейнера
    const containerHeight = useMemo(() => {
        return Math.max(pagedResources.length * ROW_HEIGHT, viewportHeight - TIME_HEADER_HEIGHT - 140);
    }, [pagedResources, viewportHeight]);

    // Форматирование метки времени
    const formatDateLabel = useCallback((item) => {
        const date = new Date(item.date);

        if (displayMode === 'years') {
            if (item.type === 'month') {
                const monthName = date.toLocaleDateString('ru-RU', { month: 'short' });
                return (
                    <div className="rg-month-label">
                        {monthName}
                    </div>
                );
            }
        } else if (displayMode === 'days') {
            const isToday = date.toDateString() === new Date().toDateString();
            const dayOfWeek = date.toLocaleDateString('ru-RU', { weekday: 'short' });
            const dayMonth = date.getDate();

            return (
                <div className={`rg-date-label ${isToday ? 'rg-today' : ''}`}>
                    <div className="rg-day-of-week">{dayOfWeek}</div>
                    <div className="rg-day-month">
                        {dayMonth}
                    </div>
                </div>
            );
        } else if (displayMode === 'hours') {
            if (item.type === 'hour') {
                const hour = item.hour;
                const isNewDay = hour === 0;

                return (
                    <div className={`rg-hour-label ${isNewDay ? 'rg-new-day' : ''}`}>
                        {hour.toString().padStart(2, '0')}:00
                        {isNewDay && (
                            <div className="rg-day-indicator">
                                {date.getDate()} {date.toLocaleDateString('ru-RU', { month: 'short' })}
                            </div>
                        )}
                    </div>
                );
            }
        }

        return null;
    }, [displayMode]);

    // Определение цвета операции по статусу
    const getOperationColor = (status) => {
        const statusNumber = Number(status);
        switch (statusNumber) {
            case 1: return '#10b981';
            case 2: return '#6b7280';
            case 3: return '#f59e0b';
            case 4: return '#ef4444';
            case 5: return '#eab308';
            case 6: return '#3b82f6';
            case 7: return '#8b5cf6';
            default: return '#6b7280';
        }
    };

    // Получение текста статуса
    const getStatusText = (status) => {
        const statusNumber = Number(status);
        switch (statusNumber) {
            case 1: return 'Выполнено';
            case 2: return 'В ожидании';
            case 3: return 'В процессе';
            case 4: return 'Ошибка';
            case 5: return 'На паузе';
            case 6: return 'Планируется';
            case 7: return 'Отменено';
            default: return 'Неизвестно';
        }
    };

    // Получение иконки для типа ресурса
    const getTypeIcon = (type) => {
        switch(type) {
            case 'personnel': return '👤';
            case 'equipment': return '🔧';
            case 'tool': return '📐';
            default: return '📦';
        }
    };

    // Получение названия типа ресурса
    const getTypeName = (type) => {
        switch(type) {
            case 'personnel': return 'Персонал';
            case 'equipment': return 'Оборудование';
            case 'tool': return 'Инструмент';
            default: return 'Другой';
        }
    };

    // Расчет позиции операции с учетом фактической длительности
    const calculateOperationPosition = useCallback((operation) => {
        if (!operation.startDate || !operation.endDate || timeLabels.length === 0) {
            return { left: 0, width: 0, isVisible: false };
        }

        try {
            const start = new Date(operation.startDate);
            const end = new Date(operation.endDate);
            const totalDurationMs = end - start;

            // Получаем точное время начала и окончания в миллисекундах
            const startTime = start.getTime();
            const endTime = end.getTime();

            let leftPos = 0;
            let width = 0;

            if (displayMode === 'years') {
                // Для режима лет/месяцев вычисляем точное положение внутри месяца

                // Находим базовый месяц начала операции
                const startMonth = new Date(start);
                startMonth.setDate(1);
                startMonth.setHours(0, 0, 0, 0);
                const startMonthTime = startMonth.getTime();

                // Находим базовый месяц окончания операции
                const endMonth = new Date(end);
                endMonth.setDate(1);
                endMonth.setHours(0, 0, 0, 0);
                const endMonthTime = endMonth.getTime();

                // Находим индексы месяцев в timeLabels
                let startMonthIndex = -1;
                let endMonthIndex = -1;

                for (let i = 0; i < timeLabels.length; i++) {
                    const label = timeLabels[i];
                    if (label.type !== 'month') continue;

                    const labelDate = new Date(label.timestamp);
                    labelDate.setDate(1);
                    labelDate.setHours(0, 0, 0, 0);
                    const labelMonthTime = labelDate.getTime();

                    if (labelMonthTime === startMonthTime) {
                        startMonthIndex = i;
                    }
                    if (labelMonthTime === endMonthTime) {
                        endMonthIndex = i;
                    }
                }

                if (startMonthIndex === -1 || endMonthIndex === -1) {
                    return { left: 0, width: 0, isVisible: false };
                }

                // Рассчитываем смещение внутри месяца начала
                const startDay = start.getDate();
                const startHour = start.getHours();
                const startMinute = start.getMinutes();
                const startSecond = start.getSeconds();

                // Получаем количество дней в месяце начала
                const startMonthDays = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();

                // Процент месяца, в который началась операция (0-1)
                const startOffsetInMonth = ((startDay - 1) * 86400 + startHour * 3600 + startMinute * 60 + startSecond) / (startMonthDays * 86400);

                // Рассчитываем смещение внутри месяца окончания
                const endDay = end.getDate();
                const endHour = end.getHours();
                const endMinute = end.getMinutes();
                const endSecond = end.getSeconds();

                // Получаем количество дней в месяце окончания
                const endMonthDays = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();

                // Процент месяца, в который закончилась операция (0-1)
                const endOffsetInMonth = ((endDay - 1) * 86400 + endHour * 3600 + endMinute * 60 + endSecond) / (endMonthDays * 86400);

                // Если операция начинается и заканчивается в один месяц
                if (startMonthIndex === endMonthIndex) {
                    leftPos = startMonthIndex * TIME_UNIT_WIDTH + startOffsetInMonth * TIME_UNIT_WIDTH;
                    width = (endOffsetInMonth - startOffsetInMonth) * TIME_UNIT_WIDTH;

                    // Минимальная ширина для видимости
                    if (width < 2) width = 2;
                } else {
                    // Операция занимает несколько месяцев
                    leftPos = startMonthIndex * TIME_UNIT_WIDTH + startOffsetInMonth * TIME_UNIT_WIDTH;

                    // Ширина в первом месяце
                    const widthFirstMonth = (1 - startOffsetInMonth) * TIME_UNIT_WIDTH;

                    // Ширина в последнем месяце
                    const widthLastMonth = endOffsetInMonth * TIME_UNIT_WIDTH;

                    // Ширина в полных месяцах между первым и последним
                    const fullMonthsWidth = (endMonthIndex - startMonthIndex - 1) * TIME_UNIT_WIDTH;

                    width = widthFirstMonth + fullMonthsWidth + widthLastMonth;
                }

            } else if (displayMode === 'days') {
                // Для режима дней вычисляем точное положение внутри дня

                // Находим базовый день начала операции
                const startDay = new Date(start);
                startDay.setHours(0, 0, 0, 0);
                const startDayTime = startDay.getTime();

                // Находим базовый день окончания операции
                const endDay = new Date(end);
                endDay.setHours(0, 0, 0, 0);
                const endDayTime = endDay.getTime();

                // Находим индексы дней в timeLabels
                let startDayIndex = -1;
                let endDayIndex = -1;

                for (let i = 0; i < timeLabels.length; i++) {
                    const label = timeLabels[i];
                    if (label.type !== 'day') continue;

                    if (label.timestamp === startDayTime) {
                        startDayIndex = i;
                    }
                    if (label.timestamp === endDayTime) {
                        endDayIndex = i;
                    }
                }

                if (startDayIndex === -1 || endDayIndex === -1) {
                    return { left: 0, width: 0, isVisible: false };
                }

                // Рассчитываем смещение внутри дня начала
                const startHour = start.getHours();
                const startMinute = start.getMinutes();
                const startSecond = start.getSeconds();

                // Процент дня, в который началась операция (0-1)
                const startOffsetInDay = (startHour * 3600 + startMinute * 60 + startSecond) / 86400;

                // Рассчитываем смещение внутри дня окончания
                const endHour = end.getHours();
                const endMinute = end.getMinutes();
                const endSecond = end.getSeconds();

                // Процент дня, в который закончилась операция (0-1)
                const endOffsetInDay = (endHour * 3600 + endMinute * 60 + endSecond) / 86400;

                // Если операция начинается и заканчивается в один день
                if (startDayIndex === endDayIndex) {
                    leftPos = startDayIndex * TIME_UNIT_WIDTH + startOffsetInDay * TIME_UNIT_WIDTH;
                    width = (endOffsetInDay - startOffsetInDay) * TIME_UNIT_WIDTH;

                    // Минимальная ширина для видимости
                    if (width < 2) width = 2;
                } else {
                    // Операция занимает несколько дней
                    leftPos = startDayIndex * TIME_UNIT_WIDTH + startOffsetInDay * TIME_UNIT_WIDTH;

                    // Ширина в первом дне
                    const widthFirstDay = (1 - startOffsetInDay) * TIME_UNIT_WIDTH;

                    // Ширина в последнем дне
                    const widthLastDay = endOffsetInDay * TIME_UNIT_WIDTH;

                    // Ширина в полных днях между первым и последним
                    const fullDaysWidth = (endDayIndex - startDayIndex - 1) * TIME_UNIT_WIDTH;

                    width = widthFirstDay + fullDaysWidth + widthLastDay;
                }

            } else if (displayMode === 'hours') {
                // Для режима часов используем по-часовую точность

                // Находим ближайшие часы
                let startIndex = -1;
                let endIndex = -1;

                for (let i = 0; i < timeLabels.length; i++) {
                    const label = timeLabels[i];
                    if (label.type !== 'hour') continue;

                    const labelTime = label.timestamp;
                    const labelEndTime = labelTime + 3600000; // +1 час

                    if (startIndex === -1 && labelEndTime > startTime) {
                        startIndex = i;
                    }
                    if (labelTime <= endTime) {
                        endIndex = i;
                    }
                }

                if (startIndex === -1 || endIndex === -1) {
                    return { left: 0, width: 0, isVisible: false };
                }

                if (startIndex > endIndex) {
                    [startIndex, endIndex] = [endIndex, startIndex];
                }

                // Рассчитываем смещение внутри часа начала
                const startMinute = start.getMinutes();
                const startSecond = start.getSeconds();
                const startOffsetInHour = (startMinute * 60 + startSecond) / 3600;

                // Рассчитываем смещение внутри часа окончания
                const endMinute = end.getMinutes();
                const endSecond = end.getSeconds();
                const endOffsetInHour = (endMinute * 60 + endSecond) / 3600;

                // Если операция начинается и заканчивается в один час
                if (startIndex === endIndex) {
                    leftPos = startIndex * TIME_UNIT_WIDTH + startOffsetInHour * TIME_UNIT_WIDTH;
                    width = (endOffsetInHour - startOffsetInHour) * TIME_UNIT_WIDTH;

                    // Минимальная ширина для видимости
                    if (width < 2) width = 2;
                } else {
                    // Операция занимает несколько часов
                    leftPos = startIndex * TIME_UNIT_WIDTH + startOffsetInHour * TIME_UNIT_WIDTH;

                    // Ширина в первом часе
                    const widthFirstHour = (1 - startOffsetInHour) * TIME_UNIT_WIDTH;

                    // Ширина в последнем часе
                    const widthLastHour = endOffsetInHour * TIME_UNIT_WIDTH;

                    // Ширина в полных часах между первым и последним
                    const fullHoursWidth = (endIndex - startIndex - 1) * TIME_UNIT_WIDTH;

                    width = widthFirstHour + fullHoursWidth + widthLastHour;
                }
            }

            // Гарантируем минимальную ширину для видимости
            const minWidth = displayMode === 'hours' ? 2 : displayMode === 'days' ? 4 : 2; // Для режима лет тоже 2px
            if (width < minWidth) {
                width = minWidth;
            }

            return {
                left: leftPos,
                width,
                isVisible: true,
                duration: totalDurationMs
            };

        } catch (error) {
            console.error('Ошибка расчета позиции:', error);
            return { left: 0, width: 0, isVisible: false };
        }
    }, [timeLabels, TIME_UNIT_WIDTH, displayMode]);

    // Группировка операций по ресурсам
    const operationsByResource = useMemo(() => {
        const map = new Map();
        pagedOperations.forEach(operation => {
            if (!map.has(operation.resourceId)) {
                map.set(operation.resourceId, []);
            }
            map.get(operation.resourceId).push(operation);
        });
        return map;
    }, [pagedOperations]);

    // Рендер вертикального грида для диаграммы - ИСПРАВЛЕНО: убраны выходные для режима лет
    const renderVerticalGrid = useCallback(() => {
        return (
            <div className="rg-vertical-grid">
                {timeLabels.map((item, index) => {
                    const date = new Date(item.date);
                    const isToday = date.toDateString() === new Date().toDateString();

                    // Для режима лет не выделяем выходные
                    const isWeekend = displayMode !== 'years' && (date.getDay() === 0 || date.getDay() === 6);

                    return (
                        <div
                            key={`v-grid-${index}`}
                            className={`rg-grid-line ${isToday ? 'rg-today' : ''} ${isWeekend ? 'rg-weekend' : ''}`}
                            style={{
                                left: `${index * TIME_UNIT_WIDTH}px`,
                                width: '1px'
                            }}
                        />
                    );
                })}
                {/* Последняя линия */}
                <div
                    className="rg-grid-line"
                    style={{
                        left: `${timeLabels.length * TIME_UNIT_WIDTH}px`,
                        width: '1px'
                    }}
                />
            </div>
        );
    }, [timeLabels, TIME_UNIT_WIDTH, displayMode]);

    // Рендер вертикального грида для временной шкалы - ИСПРАВЛЕНО: убраны выходные для режима лет
    const renderTimeScaleVerticalGrid = useCallback(() => {
        return (
            <div className="rg-time-scale-vertical-grid">
                {timeLabels.map((item, index) => {
                    const date = new Date(item.date);
                    const isToday = date.toDateString() === new Date().toDateString();

                    // Для режима лет не выделяем выходные
                    const isWeekend = displayMode !== 'years' && (date.getDay() === 0 || date.getDay() === 6);

                    return (
                        <div
                            key={`ts-v-grid-${index}`}
                            className={`rg-grid-line ${isToday ? 'rg-today' : ''} ${isWeekend ? 'rg-weekend' : ''}`}
                            style={{
                                left: `${index * TIME_UNIT_WIDTH}px`,
                                width: '1px'
                            }}
                        />
                    );
                })}
                {/* Последняя линия */}
                <div
                    className="rg-grid-line"
                    style={{
                        left: `${timeLabels.length * TIME_UNIT_WIDTH}px`,
                        width: '1px'
                    }}
                />
            </div>
        );
    }, [timeLabels, TIME_UNIT_WIDTH, displayMode]);

    // Рендер горизонтального грида
    const renderHorizontalGrid = useCallback(() => {
        return (
            <div className="rg-horizontal-grid">
                {pagedResources.map((resource, index) => (
                    <div
                        key={`h-grid-${resource.id}`}
                        className="rg-grid-line"
                        style={{
                            top: `${index * ROW_HEIGHT}px`,
                            height: '1px'
                        }}
                    />
                ))}
                {/* Последняя линия */}
                <div
                    className="rg-grid-line"
                    style={{
                        top: `${pagedResources.length * ROW_HEIGHT}px`,
                        height: '1px'
                    }}
                />
            </div>
        );
    }, [pagedResources, ROW_HEIGHT]);

    // Рендер временных меток в стиле GanttCalendar - ИСПРАВЛЕНО: добавлены заголовки годов для режима лет
    const renderTimeLabels = useCallback(() => {
        if (displayMode === 'days') {
            return (
                <div
                    className="rg-time-scale-container"
                    style={{ width: `${containerWidth}px`, minWidth: `${containerWidth}px` }}
                >
                    {/* Вертикальный грид для временной шкалы */}
                    {renderTimeScaleVerticalGrid()}

                    {/* Горизонтальные линии для временной шкалы */}
                    <div className="rg-time-scale-horizontal-grid">
                        <div className="rg-grid-line" style={{ top: '40px', height: '1px', width: '100%' }} />
                        <div className="rg-grid-line" style={{ top: '90px', height: '1px', width: '100%' }} />
                    </div>

                    {/* Месячные заголовки */}
                    <div className="rg-month-headers">
                        {monthHeaders.map((header, index) => {
                            const left = header.startIndex * TIME_UNIT_WIDTH;
                            const width = header.daysCount * TIME_UNIT_WIDTH;

                            return (
                                <div
                                    key={`month-${index}`}
                                    className="rg-month-header"
                                    style={{
                                        left: `${left}px`,
                                        width: `${width}px`,
                                        minWidth: `${width}px`
                                    }}
                                >
                                    {formatMonthHeader(header.month)}
                                </div>
                            );
                        })}
                    </div>

                    {/* Дни недели */}
                    <div
                        className="rg-day-headers"
                        style={{
                            position: 'absolute',
                            top: '40px',
                            left: 0,
                            width: `${containerWidth}px`,
                            minWidth: `${containerWidth}px`
                        }}
                    >
                        {timeLabels.map((item, index) => {
                            if (item.type !== 'day') return null;

                            const date = new Date(item.date);
                            const isToday = date.toDateString() === new Date().toDateString();
                            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                            return (
                                <div
                                    key={`time-${index}`}
                                    className={`rg-time-cell ${isToday ? 'rg-today' : ''} ${isWeekend ? 'rg-weekend' : ''}`}
                                    style={{
                                        left: `${index * TIME_UNIT_WIDTH}px`,
                                        width: `${TIME_UNIT_WIDTH}px`,
                                        minWidth: `${TIME_UNIT_WIDTH}px`,
                                        position: 'absolute'
                                    }}
                                >
                                    <div className="rg-time-label-wrapper">
                                        <div className="rg-day-of-week">
                                            {date.toLocaleDateString('ru-RU', { weekday: 'short' }).toLowerCase()}
                                        </div>
                                        <div className="rg-day-month">
                                            {date.getDate()}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        } else if (displayMode === 'years') {
            // Для режима лет используем формат с годами
            return (
                <div
                    className="rg-time-scale-container"
                    style={{ width: `${containerWidth}px`, minWidth: `${containerWidth}px` }}
                >
                    {/* Вертикальный грид для временной шкалы */}
                    {renderTimeScaleVerticalGrid()}

                    {/* Горизонтальные линии для временной шкалы */}
                    <div className="rg-time-scale-horizontal-grid">
                        <div className="rg-grid-line" style={{ top: '40px', height: '1px', width: '100%' }} />
                        <div className="rg-grid-line" style={{ top: '90px', height: '1px', width: '100%' }} />
                    </div>

                    {/* Годовые заголовки */}
                    <div className="rg-month-headers">
                        {yearHeaders.map((header, index) => {
                            const left = header.startIndex * TIME_UNIT_WIDTH;
                            const width = header.monthsCount * TIME_UNIT_WIDTH;

                            return (
                                <div
                                    key={`year-${index}`}
                                    className="rg-month-header"
                                    style={{
                                        left: `${left}px`,
                                        width: `${width}px`,
                                        minWidth: `${width}px`
                                    }}
                                >
                                    {formatYearHeader(header.year)}
                                </div>
                            );
                        })}
                    </div>

                    {/* Месяцы */}
                    <div
                        className="rg-day-headers"
                        style={{
                            position: 'absolute',
                            top: '40px',
                            left: 0,
                            width: `${containerWidth}px`,
                            minWidth: `${containerWidth}px`
                        }}
                    >
                        {timeLabels.map((item, index) => {
                            if (item.type !== 'month') return null;

                            const date = new Date(item.date);
                            const isToday = date.toDateString() === new Date().toDateString();

                            return (
                                <div
                                    key={`time-${index}`}
                                    className={`rg-time-cell ${isToday ? 'rg-today' : ''}`}
                                    style={{
                                        left: `${index * TIME_UNIT_WIDTH}px`,
                                        width: `${TIME_UNIT_WIDTH}px`,
                                        minWidth: `${TIME_UNIT_WIDTH}px`,
                                        position: 'absolute'
                                    }}
                                >
                                    <div className="rg-time-label-wrapper">
                                        <div className="rg-month-label">
                                            {date.toLocaleDateString('ru-RU', { month: 'short' })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        } else {
            // Для режима часов используем старый формат
            return (
                <div
                    className="rg-time-scale-container"
                    style={{ width: `${containerWidth}px`, minWidth: `${containerWidth}px` }}
                >
                    {/* Вертикальный грид для временной шкалы */}
                    {renderTimeScaleVerticalGrid()}

                    <div
                        className="rg-time-header"
                        style={{
                            width: `${containerWidth}px`,
                            minWidth: `${containerWidth}px`
                        }}
                    >
                        {timeLabels.map((item, index) => {
                            const date = new Date(item.date);
                            const isToday = date.toDateString() === new Date().toDateString();

                            // Для режима лет не выделяем выходные
                            const isWeekend = displayMode !== 'years' && (date.getDay() === 0 || date.getDay() === 6);

                            return (
                                <div
                                    key={`time-${index}`}
                                    className={`rg-time-cell ${isToday ? 'rg-today' : ''} ${isWeekend ? 'rg-weekend' : ''}`}
                                    style={{
                                        width: `${TIME_UNIT_WIDTH}px`,
                                        minWidth: `${TIME_UNIT_WIDTH}px`
                                    }}
                                >
                                    <div className="rg-time-label-wrapper">
                                        {formatDateLabel(item)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
    }, [timeLabels, TIME_UNIT_WIDTH, containerWidth, formatDateLabel, displayMode, monthHeaders, yearHeaders, formatMonthHeader, formatYearHeader, renderTimeScaleVerticalGrid]);

    // Рендер ресурсов
    const renderResources = useCallback(() => {
        return (
            <div
                className="rg-resource-panel"
                style={{ height: `${containerHeight}px` }}
            >
                {pagedResources.map((resource) => {
                    const operationCount = operationsByResource.get(resource.id)?.length || 0;

                    return (
                        <div
                            key={resource.id}
                            className="rg-resource-row"
                            style={{ height: `${ROW_HEIGHT}px` }}
                            onClick={(e) => {
                                if (e.detail === 2) {
                                    e.stopPropagation();
                                    setSelectedResource(resource);
                                } else if (onResourceClick) {
                                    onResourceClick(resource);
                                }
                            }}
                            onDoubleClick={(e) => {
                                e.stopPropagation();
                                setSelectedResource(resource);
                            }}
                        >
                            <div className="rg-resource-cell">
                                <div className={`rg-resource-icon rg-resource-icon-${resource.type}`}>
                                    {getTypeIcon(resource.type)}
                                </div>
                                <div className="rg-resource-info">
                                    <div className="rg-resource-title" title={resource.title}>
                                        {resource.title}
                                    </div>
                                    <div className="rg-resource-meta">
                                        {getTypeName(resource.type)} • ID: {resource.id.substring(0, 8)}...
                                    </div>
                                </div>
                                <div className="rg-resource-stats">
                                    <span className="rg-operation-count">
                                        {operationCount} оп.
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }, [pagedResources, operationsByResource, containerHeight, getTypeIcon, getTypeName, onResourceClick]);

    // Рендер операций
    // Рендер операций
    const renderOperations = useCallback(() => {
        const operationElements = [];

        // ⛔️ Защита: если операций на странице слишком много — рендерим только первые N
        const MAX_OPERATIONS_TO_RENDER = 500;
        let totalRendered = 0;

        pagedResources.forEach((resource, resourceIndex) => {
            if (totalRendered >= MAX_OPERATIONS_TO_RENDER) return;

            const resourceOperations = operationsByResource.get(resource.id) || [];

            resourceOperations.forEach((operation) => {
                if (totalRendered >= MAX_OPERATIONS_TO_RENDER) return;
                if (!operation.startDate || !operation.endDate) return;

                const position = calculateOperationPosition(operation);
                if (!position.isVisible) return;

                const color = getOperationColor(operation.status);
                const top = resourceIndex * ROW_HEIGHT + (ROW_HEIGHT - 40) / 2;

                // Длительность
                const durationHours = position.duration / (1000 * 60 * 60);
                let durationText = '';
                if (durationHours < 24) {
                    durationText = `${Math.round(durationHours * 10) / 10} ч`;
                } else if (durationHours < 24 * 30) {
                    durationText = `${Math.round(durationHours / 24 * 10) / 10} дн`;
                } else {
                    durationText = `${Math.round(durationHours / (24 * 30) * 10) / 10} мес`;
                }

                operationElements.push(
                    <div
                        key={`op-${resource.id}-${operation.id}`}
                        className="rg-operation"
                        style={{
                            left: `${position.left}px`,
                            width: `${position.width}px`,
                            top: `${top}px`,
                            height: '40px',
                            backgroundColor: color,
                            minWidth: displayMode === 'hours' || displayMode === 'years' ? '2px' : '4px'
                        }}
                        onClick={(e) => {
                            if (e.detail === 2) {
                                e.stopPropagation();
                                setSelectedOperation(operation);
                            } else if (onOperationClick) {
                                onOperationClick(operation);
                            }
                        }}
                        onDoubleClick={(e) => {
                            e.stopPropagation();
                            setSelectedOperation(operation);
                        }}
                        title={
                            `${operation.title || 'Операция'}\n` +
                            `Статус: ${getStatusText(operation.status)}\n` +
                            `Начало: ${new Date(operation.startDate).toLocaleString('ru-RU')}\n` +
                            `Окончание: ${new Date(operation.endDate).toLocaleString('ru-RU')}\n` +
                            `Длительность: ${durationText}`
                        }
                    >
                        <div className="rg-operation-content">
                            {position.width > TIME_UNIT_WIDTH * 0.8 && (
                                <span className="rg-operation-title">
                                    {operation.title || operation.operationNumber || 'Оп.'}
                                </span>
                            )}
                        </div>
                    </div>
                );

                totalRendered++;
            });
        });

        if (totalRendered >= MAX_OPERATIONS_TO_RENDER) {
            console.warn(
                `⚠️ Показано только ${MAX_OPERATIONS_TO_RENDER} операций из возможных. ` +
                `Уменьшите pageSize или используйте фильтры.`
            );
        }

        console.log('🔍 Отрисовано операций:', totalRendered);

        return operationElements;
    }, [pagedResources, operationsByResource, calculateOperationPosition, getOperationColor, getStatusText, onOperationClick, TIME_UNIT_WIDTH, displayMode]);
    // Рендер активных фильтров
    const renderActiveFilters = () => {
        if (activeFilters.length === 0) {
            return (
                <div className="rg-no-filters">
                    <span className="rg-no-filters-icon">🔍</span>
                    <span>Нет активных фильтров</span>
                </div>
            );
        }

        return activeFilters.map(filter => (
            <div
                key={filter.id}
                className="rg-filter-tab rg-filter-tab-active"
                onClick={() => {
                    // При клике на активный фильтр делаем его выделенным
                    // В реальном приложении можно добавить дополнительные действия
                }}
            >
                <span className="rg-filter-icon">{filter.icon}</span>
                <span className="rg-filter-text" title={filter.label}>
                    {filter.label}
                </span>
                <button
                    className="rg-filter-close"
                    onClick={(e) => {
                        e.stopPropagation();
                        removeFilter(filter.id);
                    }}
                    title="Удалить фильтр"
                >
                    ×
                </button>
            </div>
        ));
    };

    // Рендер выпадающего меню фильтра
    const renderFilterDropdown = () => {
        if (!openDropdown) return null;

        const options = filterOptions[openDropdown] || [];
        const title = openDropdown === FILTER_TYPES.TYPE ? 'Тип ресурса' :
            openDropdown === FILTER_TYPES.STATUS ? 'Статус операций' :
                openDropdown === FILTER_TYPES.OPERATION_COUNT ? 'Количество операций' : 'Фильтр';

        return (
            <>
                <div
                    className="rg-dropdown-backdrop"
                    onClick={() => setOpenDropdown(null)}
                />
                <div
                    ref={dropdownRef}
                    className={`rg-filter-dropdown rg-filter-dropdown-open`}
                    style={{
                        position: 'fixed',
                        left: `${dropdownPosition.x}px`,
                        top: `${dropdownPosition.y}px`,
                        zIndex: 1000
                    }}
                >
                    <div className="rg-filter-dropdown-header">
                        {title}
                    </div>
                    <div className="rg-filter-dropdown-content">
                        {options.map(option => {
                            const isActive = isFilterActive(openDropdown, option.id);
                            return (
                                <div
                                    key={option.id}
                                    className={`rg-filter-dropdown-item ${isActive ? 'rg-filter-dropdown-item-active' : ''}`}
                                    onClick={() => {
                                        if (isActive) {
                                            // Удаляем фильтр, если он уже активен
                                            setActiveFilters(prev =>
                                                prev.filter(filter =>
                                                    !(filter.type === openDropdown && filter.value === option.id)
                                                )
                                            );
                                        } else {
                                            // Добавляем фильтр
                                            addFilter(
                                                openDropdown,
                                                option.id,
                                                option.label,
                                                option.icon
                                            );
                                        }
                                    }}
                                >
                                    <div className="rg-filter-dropdown-checkbox" />
                                    <span className="rg-filter-dropdown-label">
                                        <span style={{ marginRight: '8px' }}>{option.icon}</span>
                                        {option.label}
                                    </span>
                                    {/* Можно добавить счетчики для опций */}
                                    {/* <span className="rg-filter-dropdown-count">42</span> */}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </>
        );
    };

    // Синхронизация вертикальной прокрутки
    useEffect(() => {
        const rightPanel = rightPanelRef.current;
        const leftPanel = leftPanelRef.current;

        if (!rightPanel || !leftPanel) return;

        const handleRightPanelScroll = () => {
            if (isSyncingScroll.current) return;

            isSyncingScroll.current = true;

            // Синхронизируем вертикальную прокрутку
            leftPanel.scrollTop = rightPanel.scrollTop;

            // Обновляем состояние прокрутки
            setScrollPosition({
                left: rightPanel.scrollLeft,
                top: rightPanel.scrollTop
            });

            requestAnimationFrame(() => {
                isSyncingScroll.current = false;
            });
        };

        rightPanel.addEventListener('scroll', handleRightPanelScroll, { passive: true });

        return () => {
            rightPanel.removeEventListener('scroll', handleRightPanelScroll);
        };
    }, []);

    // Начальная прокрутка к нужной дате - теперь для всех режимов
    useEffect(() => {
        if (!initialScrollDone && rightPanelRef.current && initialDateIndex > 0) {
            // Рассчитываем позицию прокрутки с учетом ширины вьюпорта
            const scrollLeft = initialDateIndex * TIME_UNIT_WIDTH - viewportWidth / 3;

            // Ждем следующего кадра, чтобы контейнеры успели отрендериться
            setTimeout(() => {
                if (rightPanelRef.current) {
                    rightPanelRef.current.scrollLeft = Math.max(0, scrollLeft);
                    setInitialScrollDone(true);
                }
            }, 100);
        }
    }, [initialDateIndex, TIME_UNIT_WIDTH, viewportWidth, displayMode, initialScrollDone]);

    // Сбрасываем флаг начальной прокрутки при изменении режима или масштаба
    useEffect(() => {
        setInitialScrollDone(false);
    }, [displayMode, zoomLevel]);

    // Обновление размеров viewport
    useEffect(() => {
        const updateViewportSize = () => {
            if (containerRef.current) {
                setViewportWidth(containerRef.current.clientWidth);
                setViewportHeight(containerRef.current.clientHeight);
            }
        };

        updateViewportSize();

        const handleResize = () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = setTimeout(updateViewportSize, 100);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    // Пагинация
    const handleNextPage = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage(prev => prev + 1);
            setInitialScrollDone(false);
            if (rightPanelRef.current) {
                rightPanelRef.current.scrollTop = 0;
                rightPanelRef.current.scrollLeft = 0;
            }
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 0) {
            setCurrentPage(prev => prev - 1);
            setInitialScrollDone(false);
            if (rightPanelRef.current) {
                rightPanelRef.current.scrollTop = 0;
                rightPanelRef.current.scrollLeft = 0;
            }
        }
    };

    const handleFirstPage = () => {
        setCurrentPage(0);
        setInitialScrollDone(false);
        if (rightPanelRef.current) {
            rightPanelRef.current.scrollTop = 0;
            rightPanelRef.current.scrollLeft = 0;
        }
    };

    const handleLastPage = () => {
        setCurrentPage(totalPages - 1);
        setInitialScrollDone(false);
        if (rightPanelRef.current) {
            rightPanelRef.current.scrollTop = 0;
            rightPanelRef.current.scrollLeft = 0;
        }
    };

    // Изменение размера страницы
    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setCurrentPage(0);
        setInitialScrollDone(false);
        if (rightPanelRef.current) {
            rightPanelRef.current.scrollTop = 0;
            rightPanelRef.current.scrollLeft = 0;
        }
    };

    // Обработчики масштабирования - теперь только три фиксированных значения
    const handleZoom75 = () => {
        setZoomLevel(ZOOM_LEVELS['75%']);
        setInitialScrollDone(false);
    };

    const handleZoom100 = () => {
        setZoomLevel(ZOOM_LEVELS['100%']);
        setInitialScrollDone(false);
    };

    const handleZoom175 = () => {
        setZoomLevel(ZOOM_LEVELS['175%']);
        setInitialScrollDone(false);
    };

    // Обработчики старых фильтров (для обратной совместимости)
    const handleSearchChange = (e) => {
        handleSearch(e.target.value);
    };

    const handleTypeFilterChange = (e) => {
        const type = e.target.value;
        if (type === 'all') {
            setTypeFilter('all');
            // Удаляем фильтр по типу
            setActiveFilters(prev => prev.filter(filter => filter.type !== FILTER_TYPES.TYPE));
        } else {
            const option = filterOptions[FILTER_TYPES.TYPE].find(opt => opt.id === type);
            if (option) {
                handleTypeFilter(type, option.label, option.icon);
            }
        }
    };

    // Проверка на пустые данные
    const hasData = sortedResources.length > 0;

    return (
        <div className="rg-container" ref={containerRef}>
            {/* Панель управления */}
            <div className="rg-controls">
                <div className="rg-controls-left">
                    <div className="rg-pagination-info">
                        <span className="rg-total-resources">
                            Всего ресурсов: {sortedResources.length}
                        </span>
                        <span className="rg-page-info">
                            | Страница: {currentPage + 1} из {totalPages}
                        </span>
                        <span className="rg-types-info">
                            | Найдено: {filteredResources.length}
                        </span>
                    </div>
                </div>

                <div className="rg-controls-right">
                    {/* Пагинация */}
                    <div className="rg-pagination-controls">
                        <button
                            onClick={handleFirstPage}
                            disabled={currentPage === 0}
                            className="rg-page-btn rg-first-btn"
                            title="Первая страница"
                        >
                            ⏮
                        </button>
                        <button
                            onClick={handlePrevPage}
                            disabled={currentPage === 0}
                            className="rg-page-btn"
                            title="Предыдущая страница"
                        >
                            ◀
                        </button>

                        <select
                            value={pageSize}
                            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                            className="rg-page-size-select"
                        >
                            <option value={20}>20 на стр.</option>
                            <option value={50}>50 на стр.</option>
                            <option value={100}>100 на стр.</option>
                            <option value={200}>200 на стр.</option>
                            <option value={500}>500 на стр.</option>
                        </select>

                        <span className="rg-page-range">
                            {Math.min(currentPage * pageSize + 1, sortedResources.length)} - {Math.min((currentPage + 1) * pageSize, sortedResources.length)}
                            <span className="rg-total-count"> из {sortedResources.length}</span>
                        </span>

                        <button
                            onClick={handleNextPage}
                            disabled={currentPage >= totalPages - 1 || totalPages === 0}
                            className="rg-page-btn"
                            title="Следующая страница"
                        >
                            ▶
                        </button>
                        <button
                            onClick={handleLastPage}
                            disabled={currentPage >= totalPages - 1 || totalPages === 0}
                            className="rg-page-btn rg-last-btn"
                            title="Последняя страница"
                        >
                            ⏭
                        </button>
                    </div>

                    {/* Масштабирование - теперь только 3 фиксированные кнопки */}
                    <div className="rg-zoom-controls">
                        <button
                            onClick={handleZoom75}
                            className={`rg-zoom-btn ${Math.abs(zoomLevel - ZOOM_LEVELS['75%']) < 0.01 ? 'rg-zoom-btn-active' : ''}`}
                            title="Масштаб 75%"
                        >
                            75%
                        </button>
                        <button
                            onClick={handleZoom100}
                            className={`rg-zoom-btn ${Math.abs(zoomLevel - ZOOM_LEVELS['100%']) < 0.01 ? 'rg-zoom-btn-active' : ''}`}
                            title="Масштаб 100%"
                        >
                            100%
                        </button>
                        <button
                            onClick={handleZoom175}
                            className={`rg-zoom-btn ${Math.abs(zoomLevel - ZOOM_LEVELS['175%']) < 0.01 ? 'rg-zoom-btn-active' : ''}`}
                            title="Масштаб 175%"
                        >
                            175%
                        </button>
                        <span className="rg-zoom-level">
                            {currentZoomPercent}
                            <span className="rg-mode-indicator">
                                {displayMode === 'years' ? ' (Годы)' :
                                    displayMode === 'days' ? ' (Дни)' :
                                        displayMode === 'hours' ? ' (Часы)' : ''}
                            </span>
                        </span>
                    </div>
                </div>
            </div>

            {/* Новые фильтры с вкладками */}
            <div className="rg-filters">
                {/* Активные фильтры */}
                <div className="rg-active-filters">
                    {renderActiveFilters()}
                </div>

                {/* Кнопки добавления фильтров */}
                <div className="rg-filter-add-buttons">
                    <div className="rg-filter-dropdown-container">
                        <button
                            ref={el => filterButtonRefs.current[FILTER_TYPES.TYPE] = el}
                            className="rg-filter-add-btn"
                            onClick={(e) => handleFilterButtonClick(FILTER_TYPES.TYPE, e)}
                        >
                            <span className="rg-add-icon">+</span>
                            <span>Тип</span>
                        </button>
                    </div>

                    <div className="rg-filter-dropdown-container">
                        <button
                            ref={el => filterButtonRefs.current[FILTER_TYPES.STATUS] = el}
                            className="rg-filter-add-btn"
                            onClick={(e) => handleFilterButtonClick(FILTER_TYPES.STATUS, e)}
                        >
                            <span className="rg-add-icon">+</span>
                            <span>Статус</span>
                        </button>
                    </div>

                    <div className="rg-filter-dropdown-container">
                        <button
                            ref={el => filterButtonRefs.current[FILTER_TYPES.OPERATION_COUNT] = el}
                            className="rg-filter-add-btn"
                            onClick={(e) => handleFilterButtonClick(FILTER_TYPES.OPERATION_COUNT, e)}
                        >
                            <span className="rg-add-icon">+</span>
                            <span>Кол-во оп.</span>
                        </button>
                    </div>

                    {/* Поиск (старый стиль) */}
                    <input
                        type="text"
                        placeholder="Поиск по названию или ID..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="rg-search-input"
                        style={{
                            background: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            color: '#374151'
                        }}
                    />

                    {/* Кнопка сброса всех фильтров */}
                    {activeFilters.length > 0 && (
                        <button
                            className="rg-clear-all-filters"
                            onClick={clearAllFilters}
                            title="Сбросить все фильтры"
                        >
                            <span className="rg-clear-icon">×</span>
                            <span>Сбросить всё</span>
                        </button>
                    )}
                </div>

                {/* Выпадающие меню фильтров */}
                {renderFilterDropdown()}
            </div>

            {/* Основная область с двумя панелями */}
            <div className="rg-main-area" ref={mainAreaRef}>
                {/* Левая панель с ресурсами - ВЕРТИКАЛЬНАЯ ПРОКРУТКА */}
                <div className="rg-left-panel-wrapper">
                    <div className="rg-left-panel-header">
                        <h3 className="rg-panel-title">Ресурсы</h3>
                        <div className="rg-resource-count">
                            {pagedResources.length} из {sortedResources.length}
                        </div>
                    </div>
                    <div
                        className="rg-resources-container"
                        ref={leftPanelRef}
                    >
                        {hasData ? renderResources() : (
                            <div className="rg-empty">
                                <div className="rg-empty-icon">🔍</div>
                                <div className="rg-empty-text">Ресурсы не найдены</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Правая панель с диаграммой - ГОРИЗОНТАЛЬНАЯ И ВЕРТИКАЛЬНАЯ ПРОКРУТКА */}
                <div className="rg-right-panel-wrapper" ref={rightPanelRef}>
                    {/* Временная шкала - ВНУТРИ ОБЩЕЙ ПРОКРУТКИ */}
                    <div
                        className="rg-time-scale-outer"
                        style={{
                            width: `${containerWidth}px`,
                            minWidth: `${containerWidth}px`,
                            height: '90px'
                        }}
                    >
                        {renderTimeLabels()}
                    </div>

                    {/* Область диаграммы - ВНУТРИ ОБЩЕЙ ПРОКРУТКИ */}
                    <div
                        className="rg-diagram-container"
                        style={{
                            width: `${containerWidth}px`,
                            minWidth: `${containerWidth}px`
                        }}
                    >
                        <div
                            className="rg-chart-container"
                            style={{
                                width: `${containerWidth}px`,
                                height: `${containerHeight}px`
                            }}
                        >
                            {/* Горизонтальный грид */}
                            {renderHorizontalGrid()}

                            {/* Вертикальный грид */}
                            {renderVerticalGrid()}

                            {/* Операции */}
                            {renderOperations()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Статусная строка */}
            <div className="rg-status-bar">
                <div className="rg-status-left">
                    <span className="rg-status-item">
                        <span className="rg-status-label">Ресурсов на странице:</span> {pagedResources.length}
                    </span>
                    <span className="rg-status-item">
                        <span className="rg-status-label">Операций:</span> {pagedOperations.length}
                    </span>
                    <span className="rg-status-item">
                        <span className="rg-status-label">Единиц времени:</span> {timeLabels.length}
                    </span>
                </div>
                <div className="rg-status-right">
                    <span className="rg-status-item">
                        <span className="rg-status-label">Режим:</span>
                        {displayMode === 'years' ? 'Годы/Месяцы' :
                            displayMode === 'days' ? 'Дни' :
                                displayMode === 'hours' ? 'Часы' : 'Неизвестно'}
                    </span>
                    <span className="rg-status-item">
                        <span className="rg-status-label">Ширина ячейки:</span> {TIME_UNIT_WIDTH.toFixed(1)}px
                    </span>
                    <span className="rg-status-item">
                        <span className="rg-status-label">Начальная дата:</span>
                        {initialScrollDate.toLocaleDateString('ru-RU')}
                    </span>
                </div>
            </div>

            {/* Модальные окна */}
            {selectedOperation && (
                <OperationModal
                    operation={selectedOperation}
                    onClose={() => setSelectedOperation(null)}
                />
            )}

            {selectedResource && (
                <ResourceModal
                    resource={selectedResource}
                    operations={operations}
                    onClose={() => setSelectedResource(null)}
                />
            )}
        </div>
    );
};

export default ResourceGanttChart;